import { Autocomplete, Box, Button, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { InsightsPriceChart } from "./StrategyPriceChart";
import { Gauge } from "@mui/x-charts/Gauge";
import { FaBitcoin, FaEthereum } from "react-icons/fa";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { de } from "date-fns/locale";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useEffect, useMemo, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { subDays, subYears } from "date-fns";
import StrategyPerformanceChart, { useChartData } from "./strategy/StrategyPerformanceChart";
import StrategyDonutChart from "./strategy/StrategyDonutChart";
import StrategySpeedometerChart from "./strategy/StrategySpeedometerChart";
import StrategyAssetRanking from "./strategy/StrategyAssetRanking";
import { computeBenchmarkReturns, computeCumulativeReturns, computeStrategyReturns, getClusterIds, MetricRuleSet, StrategyMetricMap, calculateBenchmarkMetrics } from "./strategy/calculation";
import { loadTimeseries, listSeries, getStrategyCategory } from '../../../data/timeseriesClient';
import { BENCHMARK_SERIES, INDICATOR_SERIES, SeriesConfig } from '../../../data/seriesConfig';
import { matchSeriesByTicker, filterSeriesByTicker } from '../../../data/seriesUtils';
import { calculateClusterMetrics, ClusterMetricMap } from "./clustering/calculation";
import { StrategyMetricsTable } from "./strategy/StrategyMetricsTable";
import { calculateAssetMetrics } from "./strategy/calculation";

const btcRatio = 0.76;
const ethRatio = 0.24;


export function useCombinedMetrics(
    combinedReturns: number[]
): StrategyMetricMap | null {
    return useMemo(() => {

        if (combinedReturns.length <= 1) {
            return null;
        }

        const returns: number[] = [];
        for (let i = 1; i < combinedReturns.length; i++) {
            const prev = combinedReturns[i - 1];
            const curr = combinedReturns[i];
            if (prev > 0 && curr > 0) {
                returns.push((curr / prev) - 1);
            }
        }

        return calculateClusterMetrics(returns);
    }, [combinedReturns]);
}

interface StrategyTabProps {
    defaultTicker?: string;
}

const StrategyTab = ({ defaultTicker }: StrategyTabProps) => {

    // ==================== STATE MANAGEMENT ====================
    const [loading, setLoading] = useState(false);

    const [indicatorLoading, setIndicatorLoading] = useState(true);
    const [indicatorValues, setIndicatorValues] = useState({
        btc: 0,
        eth: 0,
        altcoin: 0
    });

    const [availableStrategies, setAvailableStrategies] = useState<SeriesConfig[]>([]);
    const [strategiesLoading, setStrategiesLoading] = useState(true);
    const [strategiesError, setStrategiesError] = useState<string | null>(null);
    const [selectedStrategy, setSelectedStrategy] = useState<SeriesConfig | null>(null);

    useEffect(() => {
        listSeries('strategy')
            .then(series => {
                setAvailableStrategies(series);
                if (series.length > 0 && !selectedStrategy) {
                    const match = defaultTicker
                        ? matchSeriesByTicker(series, defaultTicker)
                        : null;
                    setSelectedStrategy(match || series[0]);
                }
            })
            .catch(err => {
                console.error('Failed to load strategy series', err);
                setStrategiesError('Failed to load available strategies');
            })
            .finally(() => setStrategiesLoading(false));
    }, []);

    const filteredStrategies = useMemo(() => {
        if (!defaultTicker) return availableStrategies;
        return filterSeriesByTicker(availableStrategies, defaultTicker);
    }, [availableStrategies, defaultTicker]);

    const [selectedPortfolio, setSelectedPortfolio] = useState<SeriesConfig | null>(null);
    const [selectedBenchmark, setSelectedBenchmark] = useState<SeriesConfig | null>(null);

    // Separate timeseries data for each selection
    const [strategyTimeseriesData, setStrategyTimeseriesData] = useState<any[]>([]);
    const [portfolioTimeseriesData, setPortfolioTimeseriesData] = useState<any[]>([]);
    const [benchmarkTimeseriesData, setBenchmarkTimeseriesData] = useState<any[]>([]);

    const [fromDate, setFromDate] = useState<Date>(subYears(new Date(), 1));
    const [toDate, setToDate] = useState<Date>(new Date());

    const [showAllMetrics, setShowAllMetrics] = useState(false);
    const [metricRules, setMetricRules] = useState<MetricRuleSet>({
        groups: [],
        groupLogic: "AND"
    });
    const [strategyAssignmentStrategy, setStrategyAssignmentStrategy] = useState<string>("sharpe");

    const [allocations, setAllocations] = useState<Record<number, number>>({});
    const [enabledStrategies, setEnabledStrategies] = useState<Record<number, boolean>>({});
    const [cumulativeReturns, setCumulativeReturns] = useState<Record<number, number>[]>([]);
    const [combinedReturns, setCombinedReturns] = useState<number[]>([]);
    const [combinedPortfolioReturns, setCombinedPortfolioReturns] = useState<number[]>([]);
    const [combinedBenchmarkReturns, setCombinedBenchmarkReturns] = useState<number[]>([]);

    const dateRangeButtons = [
        { label: "30 days", days: 30 },
        { label: "60 days", days: 60 },
        { label: "90 days", days: 90 },
        { label: "180 days", days: 180 },
        { label: "1 year", days: 365 },
        { label: "2 years", days: 730 },
        { label: "5 years", days: 1825 },
        { label: "All", days: 10000 }
    ];

    useEffect(() => {
        const fetchIndicators = async () => {
            setIndicatorLoading(true);

            try {
                const results = await Promise.all(
                    INDICATOR_SERIES.map(series => loadTimeseries(series.series))
                );

                const values: any = {};
                INDICATOR_SERIES.forEach((series, i) => {
                    const rows = results[i];
                    if (rows.length > 0) {
                        const latestRow = rows[rows.length - 1];
                        values[series.key] = series.getValue(latestRow.data) || 0;
                    }
                });

                setIndicatorValues(values);
            } catch (error) {
                console.error("Error fetching indicator data", error);
            } finally {
                setIndicatorLoading(false);
            }
        };

        fetchIndicators();
    }, []);

    useEffect(() => {
        if (!selectedStrategy) return;

        const controller = new AbortController();
        setLoading(true);

        const endOfDay = new Date(toDate);
        endOfDay.setHours(23, 59, 59, 999);
        const opts = { from: fromDate, to: endOfDay, signal: controller.signal };

        Promise.all([
            loadTimeseries(selectedStrategy.series, opts),
            selectedPortfolio ? loadTimeseries(selectedPortfolio.series, opts) : Promise.resolve([]),
            selectedBenchmark ? loadTimeseries(selectedBenchmark.series, opts) : Promise.resolve([]),
        ])
            .then(([strategy, portfolio, benchmark]) => {
                setStrategyTimeseriesData(strategy);
                setPortfolioTimeseriesData(portfolio);
                setBenchmarkTimeseriesData(benchmark);
            })
            .catch(err => { if (err.name !== 'AbortError') console.error("Error fetching strategy timeseries data", err); })
            .finally(() => { if (!controller.signal.aborted) setLoading(false); });

        return () => controller.abort();
    }, [selectedStrategy, selectedPortfolio, selectedBenchmark, fromDate, toDate]);

    // Available assignment strategies
    const availableAssignmentStrategies = useMemo(() => {
        if (strategyTimeseriesData.length === 0) return ["sharpe"];

        const lastDataPoint = strategyTimeseriesData[strategyTimeseriesData.length - 1];
        const clusterAssignment = lastDataPoint?.data?.cluster_assignment;

        if (clusterAssignment && typeof clusterAssignment === "object") {
            return Object.keys(clusterAssignment);
        }

        return ["sharpe"];
    }, [strategyTimeseriesData]);

    useEffect(() => {
        if (availableAssignmentStrategies.length > 0 && !availableAssignmentStrategies.includes(strategyAssignmentStrategy)) {
            setStrategyAssignmentStrategy(availableAssignmentStrategies[0]);
        }
    }, [availableAssignmentStrategies, strategyAssignmentStrategy]);

    // Compute cluster return series for strategy
    const strategyReturnSeries = useMemo(() => {
        return computeStrategyReturns(strategyTimeseriesData, strategyAssignmentStrategy);
    }, [strategyTimeseriesData, strategyAssignmentStrategy]
    );

    // Compute cluster return series for portfolio
    const portfolioStrategyReturnSeries = useMemo(() => {
        if (portfolioTimeseriesData.length === 0) return null;
        return computeStrategyReturns(portfolioTimeseriesData, strategyAssignmentStrategy);
    }, [portfolioTimeseriesData, strategyAssignmentStrategy]);

    // Compute cluster return series for benchmark
    const benchmarkClusterReturnSeries = useMemo(() => {
        if (benchmarkTimeseriesData.length === 0) return null;
        return computeBenchmarkReturns(benchmarkTimeseriesData);
    }, [benchmarkTimeseriesData, strategyAssignmentStrategy]);

    const [turnoverCost, setTurnoverCost] = useState<number>(0);

    // Calculate cumulative returns for strategy
    useEffect(() => {
        const { combined: combinedReturnData } = computeCumulativeReturns(
            strategyReturnSeries.timeseriesData,
            turnoverCost
        );

        setCombinedReturns(combinedReturnData);
    }, [strategyReturnSeries, allocations, enabledStrategies, metricRules, turnoverCost]);

    // Calculate cumulative returns for portfolio
    useEffect(() => {
        if (!portfolioStrategyReturnSeries) {
            setCombinedPortfolioReturns([]);
        } else {
            const { combined: combinedPortfolioReturnData } = computeCumulativeReturns(
                portfolioStrategyReturnSeries.timeseriesData,
                turnoverCost
            );
            setCombinedPortfolioReturns(combinedPortfolioReturnData);
        }

    }, [portfolioStrategyReturnSeries, allocations, enabledStrategies, metricRules, turnoverCost]);

    // Calculate cumulative returns for benchmark
    useEffect(() => {
        if (!benchmarkClusterReturnSeries) {
            setCombinedBenchmarkReturns([]);
        } else {
            const { combined: combinedBenchmarkReturnData } = computeCumulativeReturns(
                benchmarkClusterReturnSeries.timeseriesData,
                turnoverCost
            );
            setCombinedBenchmarkReturns(combinedBenchmarkReturnData);
        }

    }, [benchmarkClusterReturnSeries, allocations, enabledStrategies, metricRules, turnoverCost]);

    // Generate chart data for strategy
    const strategyChartData = useChartData({
        combinedReturns,
        ...strategyReturnSeries
    });

    // Generate chart data for portfolio - always call hook, but with default values if no data
    const portfolioChartData = useChartData({
        combinedReturns: portfolioStrategyReturnSeries ? combinedPortfolioReturns : [],
        firstPrices: portfolioStrategyReturnSeries?.firstPrices || {},
        timeseriesData: portfolioStrategyReturnSeries?.timeseriesData || []
    });

    // Generate chart data for benchmark - always call hook, but with default values if no data
    const benchmarkChartData = useChartData({
        combinedReturns: benchmarkClusterReturnSeries ? combinedBenchmarkReturns : [],
        firstPrices: benchmarkClusterReturnSeries?.firstPrices || {},
        timeseriesData: benchmarkClusterReturnSeries?.timeseriesData || []
    });

    // Calculate metrics only when cumulative returns are properly computed
    const combinedMetrics = useCombinedMetrics(combinedReturns);
    const combinedPortfolioMetrics = useCombinedMetrics(combinedPortfolioReturns);
    const hasEnabledRule = useMemo(() => {
        return metricRules.groups.some(group =>
            group.rules.some(rule => rule.enabled)
        );
    }, [metricRules]);

    // Calculate asset metrics
    const { totalMetrics, assetMetrics } = useMemo(() => {
        if (strategyReturnSeries.timeseriesData.length === 0 || strategyTimeseriesData.length === 0) {
            return {
                totalMetrics: {
                    profitLoss: 0,
                    profitLossPercent: 0,
                    totalReturn: 0,
                    sharpeRatio: 0,
                    maxDrawdown: 0,
                    volatility: 0,
                    averageReturn: 0,
                    winRate: 0,
                    timeHeld: 0,
                },
                assetMetrics: []
            };
        }
        return calculateAssetMetrics(strategyReturnSeries.timeseriesData, strategyTimeseriesData);
    }, [strategyReturnSeries, strategyTimeseriesData]);

    const benchmarkMetrics = useMemo(() => {
        if (!benchmarkTimeseriesData || benchmarkTimeseriesData.length === 0) {
            return {
                profitLoss: 0,
                profitLossPercent: 0,
                totalReturn: 0,
                sharpeRatio: 0,
                maxDrawdown: 0,
                volatility: 0,
                averageReturn: 0,
                winRate: 0,
                timeHeld: 0
            };
        }
        return calculateBenchmarkMetrics(benchmarkClusterReturnSeries?.timeseriesData ?? [], 0);
    }, [benchmarkTimeseriesData]);

    const handleDateRangeClick = (days: number) => {
        const end = new Date();
        if (days >= 10000) {
            setFromDate(subYears(end, 10));
        } else {
            setFromDate(subDays(end, days));
        }
        setToDate(end);
    };

    const handleSliderChange = (cluster: number, value: number) => {
        setAllocations((prev) => ({ ...prev, [cluster]: value }));
    };

    const handleInputChange = (cluster: number, raw: string) => {
        const parsed = parseFloat(raw);
        setAllocations((prev) => ({
            ...prev,
            [cluster]: isNaN(parsed) ? 0 : parsed
        }));
    };

    const handleCheckboxChange = (cluster: number, checked: boolean) => {
        setEnabledStrategies((prev) => ({ ...prev, [cluster]: checked }));
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
            <Box pt={2} px={2}>
                {/*{!loading && strategyTimeseriesData && strategyTimeseriesData.length > 0 && selectedStrategy && (*/}
                {/* Conditional loading / content */}
                {indicatorLoading ? (
                    <Box display="flex" alignItems="center" justifyContent="center" height="30vh">
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Box>
                            <Grid container spacing={4}>
                                {/*<Grid item xs={12} md={4}>*/}
                                {/*    <StrategyDonutChart />*/}
                                {/*</Grid>*/}
                                <Grid item xs={12} md={4}>
                                    <StrategySpeedometerChart
                                        title="BTC"
                                        value={indicatorValues.btc}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <StrategySpeedometerChart
                                        title="ETH"
                                        value={indicatorValues.eth}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <StrategySpeedometerChart
                                        title="Altcoin"
                                        value={indicatorValues.altcoin}
                                    />
                                </Grid>
                                {/*<Grid item xs={12} md={4}>*/}
                                {/*    <StrategyAssetRanking />*/}
                                {/*</Grid>*/}
                            </Grid>
                        </Box>
                    </>
                )
                }
                {/*)}*/}

                <Box mb={3}>
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Autocomplete
                                options={filteredStrategies}
                                groupBy={(option) => getStrategyCategory(option.id)}
                                getOptionLabel={(option) => option.name}
                                value={selectedStrategy}
                                onChange={(_, newValue) => {
                                    if (newValue) {
                                        setSelectedStrategy(newValue);
                                        setStrategyTimeseriesData([]);
                                    }
                                }}
                                loading={strategiesLoading}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => (
                                    <TextField {...params} label="Strategy" />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Autocomplete
                                options={filteredStrategies}
                                groupBy={(option) => getStrategyCategory(option.id)}
                                getOptionLabel={(option) => option.name}
                                value={selectedPortfolio}
                                onChange={(_, newValue) => {
                                    setSelectedPortfolio(newValue);
                                    setPortfolioTimeseriesData([]);
                                }}
                                loading={strategiesLoading}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => (
                                    <TextField {...params} label="Portfolio" />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel id="benchmark-select-label">Benchmark</InputLabel>
                                <Select
                                    labelId="benchmark-select-label"
                                    value={selectedBenchmark?.id || ""}
                                    onChange={(e) => {
                                        const selected = BENCHMARK_SERIES.find(c => c.id === e.target.value);
                                        setSelectedBenchmark(selected || null);
                                        setBenchmarkTimeseriesData([]);
                                    }}
                                    label="Benchmark"
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {BENCHMARK_SERIES.map((benchmark) => (
                                        <MenuItem key={benchmark.id} value={benchmark.id}>
                                            {benchmark.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={6} md={4}>
                            <DatePicker
                                label="From"
                                value={fromDate}
                                onChange={(newValue: Date | null) => {
                                    if (newValue) setFromDate(newValue);
                                }}
                            />
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <DatePicker
                                label="To"
                                value={toDate}
                                onChange={(newValue: Date | null) => {
                                    if (newValue) setToDate(newValue);
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Box display="flex" gap={1}>
                                    {dateRangeButtons.slice(0, 4).map((range) => (
                                        <Button
                                            key={range.days}
                                            variant="outlined"
                                            size="small"
                                            sx={{ width: 90 }}
                                            onClick={() => handleDateRangeClick(range.days)}
                                        >
                                            {range.label}
                                        </Button>
                                    ))}
                                </Box>
                                <Box display="flex" gap={1}>
                                    {dateRangeButtons.slice(4, 8).map((range) => (
                                        <Button
                                            key={range.days}
                                            variant="outlined"
                                            size="small"
                                            sx={{ width: 90 }}
                                            onClick={() => handleDateRangeClick(range.days)}
                                        >
                                            {range.label}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Conditional loading / content */}
                {strategiesError ? (
                    <Box display="flex" alignItems="center" justifyContent="center" height="60vh">
                        <Typography color="error">{strategiesError}</Typography>
                    </Box>
                ) : loading || !selectedStrategy || !strategyTimeseriesData || strategyTimeseriesData.length === 0 ? (
                    <Box display="flex" alignItems="center" justifyContent="center" height="60vh">
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Box mb={4}>
                            <StrategyPerformanceChart
                                clustering={selectedStrategy}
                                clusteringAssignment={strategyAssignmentStrategy}
                                cumulativeReturns={cumulativeReturns}
                                enabledClusters={enabledStrategies}
                                combinedReturns={combinedReturns}
                                strategyChartData={strategyChartData}
                                portfolioChartData={portfolioChartData}
                                benchmarkChartData={benchmarkChartData}
                            />
                        </Box>

                        <StrategyMetricsTable
                            strategyName={selectedStrategy?.name}
                            benchmarkMetrics={benchmarkMetrics}
                            totalMetrics={totalMetrics}
                            assetMetrics={assetMetrics}
                        />
                    </>
                )
                }
            </Box>
        </LocalizationProvider>
    );
};

export default StrategyTab;
